/**
 * Campaign quota tracking utilities using visitor fingerprinting
 * IP-based tracking alternative using browser characteristics
 */

/**
 * Generate a unique visitor ID based on browser fingerprint
 * This creates a consistent ID for the same browser/device
 */
export const getVisitorId = (): string => {
  const fingerprint = [
    navigator.userAgent,
    navigator.language,
    screen.width,
    screen.height,
    new Date().getTimezoneOffset(),
    !!window.sessionStorage,
    !!window.localStorage,
  ].join('|');
  
  // Simple hash function
  let hash = 0;
  for (let i = 0; i < fingerprint.length; i++) {
    const char = fingerprint.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  
  return `visitor_${Math.abs(hash)}`;
};

/**
 * Get the campaign quota for the current visitor
 * Returns the current quota and handles decrement logic
 */
export const getCampaignQuota = (): number => {
  // Sabit kontenjan: tutarlılık için her zaman 5 yeni firma.
  return 5;
};

/**
 * Decrement the quota for the current visitor
 * Called when the user leaves the page
 */
export const decrementQuota = (): void => {
  const visitorId = getVisitorId();
  const storageKey = `campaign_quota_${visitorId}`;
  
  try {
    const stored = localStorage.getItem(storageKey);
    
    if (stored) {
      const data = JSON.parse(stored);
      const newQuota = Math.max(3, data.quota - 1); // Minimum 3
      
      localStorage.setItem(storageKey, JSON.stringify({
        ...data,
        quota: newQuota,
        lastVisit: Date.now()
      }));
    }
  } catch (error) {
    console.warn('Campaign quota: Failed to decrement', error);
  }
};

/**
 * Setup quota decrement on page unload
 * This ensures quota decreases when visitor leaves
 */
export const setupQuotaTracking = (): (() => void) => {
  const handleBeforeUnload = () => {
    decrementQuota();
  };

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      decrementQuota();
    }
  };

  window.addEventListener('beforeunload', handleBeforeUnload);
  document.addEventListener('visibilitychange', handleVisibilityChange);

  // Return cleanup function
  return () => {
    window.removeEventListener('beforeunload', handleBeforeUnload);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
};
